/**
 * Agentic Workflows Types
 *
 * TypeScript interfaces for the agentic workflow system that powers
 * deep research, formatting, and translation capabilities.
 */

import type { TokenUsageMetadata, StreamingMessageHelpers, SystemMessageInput, ChatMessage } from '../../components/GlobalChatSystem/types';

// ============================================================================
// Legal Niche Types
// ============================================================================

/** Legal practice area niches for deep research prompt routing */
export type LegalNiche = 'immigration' | 'workers_compensation' | 'general';

/** Valid niche values for runtime validation */
export const LEGAL_NICHES: readonly LegalNiche[] = ['immigration', 'workers_compensation', 'general'] as const;

// ============================================================================
// Deep Research Session Types
// ============================================================================

/**
 * Represents the current state of a deep research session.
 * Tracks the multi-stage research flow from query to final report.
 */
export interface DeepResearchSession {
  stage: 'idle' | 'stage1' | 'stage1_confirm' | 'stage2' | 'stage2_confirm' | 'stage3';
  originalQuery: string;
  userClarifications: string[];
  stage1Findings: string;
  stage1Questions: string[];
  stage2Brief: string;
  cancelled: boolean;
  /** Accumulated text content from user-attached documents */
  attachmentContent: string;
  /** Legal niche determined by Stage 2 for prompt routing */
  legalNiche?: LegalNiche;
}

/**
 * Initial state for a deep research session.
 */
export const INITIAL_DR_SESSION: DeepResearchSession = {
  stage: 'idle',
  originalQuery: '',
  userClarifications: [],
  stage1Findings: '',
  stage1Questions: [],
  stage2Brief: '',
  cancelled: false,
  attachmentContent: '',
};

// ============================================================================
// Report & Content Types
// ============================================================================

/**
 * Represents a completed research report.
 */
export interface ResearchReport {
  content: string;
  title: string;
  timestamp: Date;
}

/**
 * Represents content that can be translated.
 * Can be research, formatted report, video script, or document analysis.
 */
export interface TranslatableContent {
  content: string;
  title: string;
  timestamp: Date;
  source: 'research' | 'formatted' | 'video_script' | 'document_analysis' | 'document_analysis_failed';
  tokenUsages?: TokenUsageMetadata[];
}

// ============================================================================
// Stage 1 Result Types
// ============================================================================

/**
 * Parsed result from Stage 1 (qualification) analysis.
 */
export interface Stage1Result {
  requiresDeepResearch: boolean;
  clarifyingQuestions: string[];
  initialContext: string;
  simpleAnswer: string;
  reasoning: string;
}

// ============================================================================
// Handler Types
// ============================================================================

/**
 * Helper functions available to workflow handlers.
 */
export interface WorkflowHelpers {
  streaming: StreamingMessageHelpers;
  addSystemMessage: (input: SystemMessageInput) => ChatMessage;
  addMessage: (message: ChatMessage) => ChatMessage;
}

/**
 * AI Transaction recorder function type.
 */
export type AITransactionRecorder = (params: {
  tokenUsage: TokenUsageMetadata;
  source: string;
  modelOverride?: string;
  summary?: string;
  meteredServerSide?: boolean;
}) => void;

/**
 * State management for the agentic workflows.
 */
export interface AgenticWorkflowState {
  drSession: DeepResearchSession;
  setDrSession: React.Dispatch<React.SetStateAction<DeepResearchSession>>;
  lastResearchReport: ResearchReport | null;
  setLastResearchReport: React.Dispatch<React.SetStateAction<ResearchReport | null>>;
  lastResearchReportRef: React.MutableRefObject<ResearchReport | null>;
  lastTranslatableContent: TranslatableContent | null;
  setLastTranslatableContent: React.Dispatch<React.SetStateAction<TranslatableContent | null>>;
  lastTranslatableContentRef: React.MutableRefObject<TranslatableContent | null>;
  isFormatting: boolean;
  setIsFormatting: React.Dispatch<React.SetStateAction<boolean>>;
  isTranslating: boolean;
  setIsTranslating: React.Dispatch<React.SetStateAction<boolean>>;
  recordTransaction: AITransactionRecorder;
}

/**
 * Configuration for creating an agentic workflow handler.
 */
export interface AgenticWorkflowConfig {
  state: AgenticWorkflowState;
  chatHelpersRef: React.MutableRefObject<WorkflowHelpers | null>;
}

// ============================================================================
// Video Script Workflow Types
// ============================================================================

/**
 * Video Script Workflow Session State
 * Tracks the two-stage workflow from analysis to script generation.
 */
export interface VideoScriptSession {
  stage: 'idle' | 'stage1_analyzing' | 'stage1_clarify' | 'stage2_generating' | 'complete';
  sourceContent: VideoScriptSourceContent | null;
  stage1Analysis: VideoScriptStage1Result | null;
  userClarifications: VideoScriptClarification[];
  stage2Output: VideoScriptStage2Result | null;
  startedAt: Date | null;
  completedAt: Date | null;
}

/**
 * Source content that initiated the video script request.
 * Can come from deep research, formatted report, translated document, or video script.
 */
export interface VideoScriptSourceContent {
  content: string;
  title: string;
  source: 'research' | 'formatted' | 'translated' | 'video_script' | 'document_analysis' | 'document_analysis_failed';
  timestamp: Date;
}

/**
 * Stage 1 analysis result - determines if clarifications are needed.
 */
export interface VideoScriptStage1Result {
  needsClarification: boolean;
  summary: VideoScriptSummary;
  clarifyingQuestions: VideoScriptClarifyingQuestion[];
  rawResponse: string;
}

/**
 * Summary of the analyzed content (extracted from Stage 1).
 */
export interface VideoScriptSummary {
  topic: string;
  targetAudience: string;
  emotionalState: string;
  platformAndLength: string;
  primaryGoalCta: string;
}

/**
 * A clarifying question with options from Stage 1.
 */
export interface VideoScriptClarifyingQuestion {
  question: string;
  whyItMatters: string;
  options: string[];
}

/**
 * User's answer to a clarifying question.
 */
export interface VideoScriptClarification {
  question: string;
  answer: string;
  timestamp: Date;
}

/**
 * Stage 2 generation result - the complete video script output.
 */
export interface VideoScriptStage2Result {
  reasoning: VideoScriptReasoning;
  script: VideoScriptContent;
  metadata: VideoScriptMetadata;
  alternativeHooks: VideoScriptAlternativeHook[];
  shortFormVersion: VideoScriptShortForm | null;
  rawResponse: string;
}

/**
 * Reasoning shown before script (visible to user).
 */
export interface VideoScriptReasoning {
  audiencePsychology: string;
  hookStrategy: string;
  contentStrategy: string;
  ctaStrategy: string;
}

/**
 * The main video script content.
 */
export interface VideoScriptContent {
  title: string;
  fullText: string;
  sections: VideoScriptSection[];
  disclaimer: string;
}

/**
 * A section of the script with timing.
 */
export interface VideoScriptSection {
  name: string;
  startTime: string;
  endTime: string;
  content: string;
}

/**
 * Script metadata for display.
 */
export interface VideoScriptMetadata {
  estimatedLengthSeconds: number;
  estimatedLengthFormatted: string;
  characterCount: number;
  wordCount: number;
  hookType: string;
  tone: string;
  emphasisPoints: string[];
}

/**
 * An alternative hook option.
 */
export interface VideoScriptAlternativeHook {
  type: string;
  content: string;
}

/**
 * Short-form version for social media.
 */
export interface VideoScriptShortForm {
  hook: string;
  keyPoint: string;
  cta: string;
  fullText: string;
  characterCount: number;
  estimatedLengthSeconds: number;
}

/**
 * Initial empty video script session state.
 */
export const INITIAL_VIDEO_SCRIPT_SESSION: VideoScriptSession = {
  stage: 'idle',
  sourceContent: null,
  stage1Analysis: null,
  userClarifications: [],
  stage2Output: null,
  startedAt: null,
  completedAt: null,
};
